"use client";
import { getMediaUrl } from "@/lib/utils";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Link from "next/link";
import LanguageToggle from "./toggle-button";
import { getServices } from "@/lib/api";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [services, setServices] = useState([]);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const servicesContainerRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        console.log("[Navbar] Fetching services data...");
        const allServices = await getServices("ar", false);
        console.log("[Navbar] Services received:", allServices?.length || 0);
  
        if (Array.isArray(allServices) && allServices.length > 0) {
          setServices(allServices);
        } else {
          console.log("[Navbar] No services returned from API");
          setServices([]);
        }
      } catch (error) {
        console.error("[Navbar] Error fetching services:", error);
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
  
    fetchServices();
  }, []); // Empty dependency array = run once on mount
  

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const heroHeight = window.innerHeight;

      setIsScrolled(scrollPosition > 50);

      setIsVisible(scrollPosition < heroHeight);

    
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close services or mobile menu when clicking/tapping outside
  useEffect(() => {
    const handleOutside = (e) => {
      try {
        const target = e.target;

        // If click is inside the main nav (including the toggle button), ignore
        if (navRef.current && navRef.current.contains(target)) return;

        if (
          isServicesOpen &&
          servicesContainerRef.current &&
          !servicesContainerRef.current.contains(target) &&
          // If mobile menu is open and click is inside mobile menu, don't close services
          !(isMobileMenuOpen && mobileMenuRef.current && mobileMenuRef.current.contains(target))
        ) {
          setIsServicesOpen(false);
        }

        if (
          isMobileMenuOpen &&
          mobileMenuRef.current &&
          !mobileMenuRef.current.contains(target)
        ) {
          setIsMobileMenuOpen(false);
          setIsServicesOpen(false);
        }
      } catch (err) {
        // ignore
      }
    };

    document.addEventListener("click", handleOutside);
    document.addEventListener("touchend", handleOutside);
    return () => {
      document.removeEventListener("click", handleOutside);
      document.removeEventListener("touchend", handleOutside);
    };
  }, [isServicesOpen, isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((prev) => {
      if (prev) setIsServicesOpen(false);
      return !prev;
    });
  };

  const toggleServices = () => {
    setIsServicesOpen(!isServicesOpen);
  };

  return (
    <>
      <AnimatePresence>
      {isVisible && (
        <motion.nav
          initial={{ opacity: 0, y: -100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -100 }}
          transition={{ duration: 0.5 }}
          ref={navRef}
          className={`fixed top-0 left-0 right-0 z-20 transition-all duration-300 ${isScrolled ? 'bg-white/60 backdrop-blur-md backdrop-saturate-150 shadow-lg' : 'bg-transparent'}`}
          style={isMobileMenuOpen ? { height: '80px', minHeight: '80px' } : {}}
        >
          <div className={`container mx-auto px-4 sm:px-6 py-4`} style={isMobileMenuOpen ? {height: '80px', minHeight: '80px', maxHeight: '80px'} : {}}>
            <div className="flex items-center justify-between">
              {/* Logo */}
              <motion.div
                className="flex items-center space-x-3"
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.3 }}
              >
                <Link href="/" className="flex items-center">
                  <img
                    src={getMediaUrl("logo.png")}
                    alt="نخلة بابل"
                    width={30}
                    height={30}
                    className={`${isScrolled ? "filter brightness-100" : "brightness-400"}`}
                    style={{ maxWidth: '100%', height: 'auto' }}
                    loading="eager"
                  />
                </Link>
              </motion.div>

              {/* Navigation Links - Desktop */}
              <div className="hidden md:flex items-center space-x-8">
                {[
                  { name: "الرئيسية", href: "/" },
                  { name: "من نحن", href: "/about" },
                  { name: "المشاريع", href: "/portfolio" },
                  { name: "الأخبار", href: "/news" },
                  { name: "اتصل بنا", href: "/contact" },
                  { name: "وظائف", href: "/careers" },
                ].map((item, index) => (
                  <motion.div
                    key={item.name}
                    whileHover={{ y: -2 }}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Link
                      href={item.href}
                      className={`font-bold transition-colors duration-300 hover:text-[#dfa428] ${
                        isScrolled ? "text-[#0a367b]" : "text-white"
                      }`}
                    >
                      {item.name}
                    </Link>
                  </motion.div>
                ))}

                {/* Services Dropdown */}
                <div className="relative" ref={servicesContainerRef}>
                  <motion.div
                    className={`flex items-center space-x-1 font-bold transition-colors duration-300 ${
                      isScrolled ? "text-[#0a367b]" : "text-white"
                    }`}
                    whileHover={{ y: -2 }}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <Link
                      href="/services"
                      className="hover:text-[#dfa428] transition-colors duration-300"
                    >
                      الخدمات
                    </Link>
                    <button
                      onMouseEnter={() => setIsServicesOpen(true)}
                      onMouseLeave={() => setIsServicesOpen(false)}
                      className={`p-1 transition-colors duration-300 ${isScrolled ? 'text-[#0a367b] hover:text-[#dfa428]' : 'text-white hover:text-[#dfa428]'}`}
                    >
                      <svg
                        className={`w-4 h-4 transition-transform duration-300 ${
                          isServicesOpen ? "rotate-180" : ""
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </motion.div>

                  <AnimatePresence>
                    {isServicesOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -20, scale: 0.95 }}
                        transition={{ duration: 0.3 }}
                        onMouseEnter={() => setIsServicesOpen(true)}
                        onMouseLeave={() => setIsServicesOpen(false)}
                        data-hide-native-cursor
                        className="absolute top-full right-0 mt-2 w-80 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden"
                      >
                        <div className="p-2">
                          {services.map((service, index) => (
                            <motion.div
                              key={service.slug || service.id || service.title || index}
                              initial={{ opacity: 0, x: -20 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                            >
                                <Link href={`/services/${service.slug}`}>
                                <motion.div
                                  className="block px-4 py-3 text-[#0a367b] font-medium hover:bg-[#dfa428]/10 rounded-xl transition-all duration-300 cursor-pointer"
                                  whileHover={{
                                    x: 5,
                                    backgroundColor: "rgba(255, 193, 7, 0.1)",
                                    transition: { duration: 0.2 },
                                  }}
                                >
                                  {service.title}
                                </motion.div>
                              </Link>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              {/* Phone Number & Language Toggle - Desktop */}
              <motion.div
                className={`hidden lg:flex items-center space-x-3 ${isScrolled ? "text-[#0a367b]" : "text-white"}`}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <a
                  href="https://wa.me/9647800800900"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold hover:underline transition "
                >
                  0780-0800-900
                </a>
                <LanguageToggle isScrolled={isScrolled} />
              </motion.div>

              {/* Mobile Controls */}
              <div className="md:hidden flex items-center space-x-3 space-x-reverse">
                {/* Language Toggle - Always visible on mobile */}
                <LanguageToggle isScrolled={isScrolled} />

                {/* Mobile Menu Button */}
                <motion.button
                  onClick={(e) => { e.stopPropagation(); toggleMobileMenu(); }}
                  className={`p-2 rounded-lg transition-colors duration-300 bg-transparent ${
                    isScrolled ? "text-[#0a367b]" : "text-white"
                  }`}
                  whileTap={{ scale: 0.95 }}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={
                        isMobileMenuOpen
                          ? "M6 18L18 6M6 6L18 18"

                          : "M4 6h16M4 12h16M4 18h16"
                      }
                    />
                  </svg>
                </motion.button>
              </div>
            </div>

            {/* Mobile Menu (moved below nav to allow independent backdrop-blur) */}
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
    {/* Mobile Menu rendered outside the nav so backdrop-blur applies correctly */}
    <AnimatePresence>
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.25 }}
          className={`fixed left-0 right-0 z-50 md:hidden px-4`}
          ref={mobileMenuRef}
          style={{ top: isVisible ? '92px' : '0px' }}
        >
          <div className={`rounded-2xl p-4 shadow-lg ${isScrolled ? 'bg-white/60 backdrop-blur-lg backdrop-saturate-150' : 'backdrop-blur-lg bg-transparent'} transition-all duration-300`}>
            {/* Main Navigation Links */}
            <div className="space-y-1">
              {[
                { name: "الرئيسية", href: "/" },
                { name: "من نحن", href: "/about" },
                { name: "المشاريع", href: "/portfolio" },
                { name: "الأخبار", href: "/news" },
                { name: "اتصل بنا", href: "/contact" },
                { name: "وظائف", href: "/careers" },
              ].map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Link
                    href={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block py-3 px-4 rounded-xl font-bold transition-all duration-300 ${isScrolled ? 'text-[#0a367b]' : 'text-white'} hover:bg-[#dfa428]/10 active:bg-[#dfa428]/20`}
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.35 }}
                className="flex items-center justify-between py-3 px-4 rounded-xl font-bold transition-all duration-300 text-white"
              >
                <Link
                  href="/services"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex-1 hover:text-[#dfa428] transition-colors duration-300 ${isScrolled ? 'text-[#0a367b]' : 'text-white'}`}
                >
                  الخدمات
                </Link>
                <button
                  onClick={(e) => { e.stopPropagation(); toggleServices(); }}
                  className={`p-1 transition-colors duration-300 ${isScrolled ? 'text-[#0a367b] hover:text-[#dfa428]' : 'text-white hover:text-[#dfa428]'}`}
                >
                  <svg
                    className={`w-4 h-4 transition-transform duration-300 ${isServicesOpen ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </motion.div>
            </div>

            <AnimatePresence>
              {isServicesOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2 }}
                  data-hide-native-cursor
                  className="mt-2 pt-2 space-y-1 border-t border-gray-200/30 overflow-hidden"
                >
                  <div className={`px-4 py-2 text-sm font-semibold ${isScrolled ? 'text-[#0a367b]' : 'text-white'}`}>
                    خدماتنا المتخصصة
                  </div>
                  {services.map((service, index) => (
                    <motion.div
                      key={service.slug || service.id || service.name || index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Link
                        href={`/services/${service.slug}`}
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          setIsServicesOpen(false);
                        }}
                        className={`block py-2.5 px-6 mr-4 rounded-xl text-sm font-medium transition-all duration-300 ${isScrolled ? 'text-[#0a367b]' : 'text-white'} hover:bg-[#dfa428]/10 hover:text-[#0a367b] active:bg-[#dfa428]/20`}
                      >
                        {service.title}
                      </Link>
                    </motion.div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>

            <div className="mt-6 pt-4 border-t border-gray-200/30">
              <motion.div
                className={`flex items-center justify-center space-x-2 mb-4 p-3 rounded-xl font-bold text-lg transition-all duration-300 ${isScrolled ? 'text-[#0a367b]' : 'text-white'} bg-[#0a367b]/5`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <svg className="w-5 h-5 text-[#dfa428]" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                <a href="https://wa.me/9647800800900" target="_blank" rel="noopener noreferrer" className={`${isScrolled ? 'text-[#0a367b]' : 'text-white'}`}>0780-0800-900</a>
              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
    </>
  );
};

export default Navbar;
